import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { allegroApiService } from '../services/allegroApi';
import { useAuth } from './AuthContext';

const ShopsContext = createContext();

export const useShops = () => {
  const context = useContext(ShopsContext);
  if (!context) {
    throw new Error('useShops must be used within a ShopsProvider');
  }
  return context;
};

export const ShopsProvider = ({ children }) => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const isLoadingRef = useRef(false); // Używamy useRef zamiast useState
  const { isAuthenticated } = useAuth();

  const loadShops = useCallback(async () => {
    if (isLoadingRef.current) {
      console.log('🔄 Shops already loading, skipping...');
      return;
    }
    
    try {
      console.log('🛍️ Loading shops...');
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      const shopsData = await allegroApiService.getShops();
      setShops(shopsData);
      console.log('✅ Shops loaded:', shopsData.length);
    } catch (err) {
      setError('Błąd podczas ładowania sklepów');
      console.error('❌ Błąd ładowania sklepów:', err);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      setIsInitialized(true);
    }
  }, []); // Usunięto zależność isLoading

  const refreshShops = useCallback(async () => {
    await loadShops();
  }, [loadShops]);

  // Load shops when user is authenticated and not already loaded
  useEffect(() => {
    console.log('🔄 ShopsContext useEffect triggered, isAuthenticated:', isAuthenticated, 'isInitialized:', isInitialized);
    if (isAuthenticated && !isInitialized) {
      loadShops();
    } else if (!isAuthenticated) {
      // Reset shops state when user logs out
      setShops([]);
      setError(null);
      setIsInitialized(false);
      isLoadingRef.current = false;
    }
  }, [isAuthenticated, isInitialized, loadShops]);

  const value = {
    shops,
    loading,
    error,
    isInitialized,
    refreshShops,
  };

  return (
    <ShopsContext.Provider value={value}>
      {children}
    </ShopsContext.Provider>
  );
}; 